import { DateTime } from "luxon";
import { getJSON } from "../../utils/request";

export const setMembers = (members) => ({
  type: "MEMBER_SET_MEMBERS",
  payload: members,
});

export const setCurrentScheduleId = (scheduleId) => ({
  type: "MEMBER_SET_CURRENT_SCHEDULE_ID",
  payload: scheduleId,
});

// Summarises the shifts a member already holds on *other* schedules. Extracted so the thunk
// above does not nest callbacks five deep.
const shiftTotals = (shifts, scheduleId) => {
  const existing = (shifts || []).filter((shift) => shift.schedule_id !== scheduleId);

  return {
    count: existing.length,
    hours: existing.reduce((total, shift) => total + shift.duration, 0),
  };
};

const withExistingShiftData = (member, scheduleId) => {
  const shifts = shiftTotals(member.shifts, scheduleId);
  const preferred = shiftTotals(member.preferred_shifts, scheduleId);

  return {
    ...member,
    existing_shift_count: shifts.count,
    existing_hour_count: shifts.hours,
    existing_preferred_shift_count: preferred.count,
    existing_preferred_hour_count: preferred.hours,
    current_shift_count: 0,
    current_hour_count: 0,
    current_preferred_shift_count: 0,
    current_preferred_hour_count: 0,
    total_shift_count: shifts.count,
    total_hour_count: shifts.hours,
    total_preferred_shift_count: preferred.count,
    total_preferred_hour_count: preferred.hours,
  };
};

export const fetchMembers = (url) => {
  return (dispatch, getState) => {
    dispatch(setLoading(true));
    return getJSON(url)
      .then((members) => {
        const sortedMembers = members.sort((a, b) =>
          a.name
            .toString()
            .toLowerCase()
            .localeCompare(b.name.toString().toLowerCase())
        );
        dispatch(setMembers(sortedMembers));
        
        // Set existing shift data based on current schedule
        const state = getState();
        const scheduleId = state.member.schedule_id;
        
        const membersWithExistingData = sortedMembers.map((member) =>
          withExistingShiftData(member, scheduleId)
        );

        dispatch(setMembers(membersWithExistingData));
        dispatch(fetchShifts('shifts.json'));
        
        return membersWithExistingData;
      })
      .catch((error) => {
        console.error("Error fetching members:", error);
        dispatch(setLoading(false));
      });
  };
};

export const setShifts = (shifts) => ({
  type: "MEMBER_SET_SHIFTS",
  payload: shifts,
});

export const updateMember = (id, updates) => ({
  type: "MEMBER_UPDATE_MEMBER",
  payload: { id, updates },
});

export const addMember = (shiftId, member) => {
  return (dispatch) => {
    dispatch({
      type: "SHIFT_ADD_MEMBER",
      payload: { shiftId, member },
    });
    dispatch(updateMemberShiftCounts());
  };
};

export const addMembers = (shiftId, members) => {
  return (dispatch) => {
    dispatch({
      type: "SHIFT_ADD_MEMBERS",
      payload: { shiftId, members },
    });
    dispatch(updateMemberShiftCounts());
  };
};

export const removeMember = (shiftId, member) => {
  return (dispatch) => {
    dispatch({
      type: "SHIFT_REMOVE_MEMBER",
      payload: { shiftId, member },
    });
    dispatch(updateMemberShiftCounts());
  };
};

export const toggleLeader = (shiftId, memberId) => {
  return (dispatch) => {
    dispatch({
      type: "SHIFT_TOGGLE_LEADER",
      payload: { shiftId, memberId },
    });
    // No need to update counts for toggle leader
  };
};

export const addShift = (shift) => {
  return (dispatch) => {
    dispatch({
      type: "SHIFT_ADD_SHIFT",
      payload: shift,
    });
    dispatch(updateMemberShiftCounts());
  };
};

export const updateShift = (shift) => {
  return (dispatch) => {
    dispatch({
      type: "SHIFT_UPDATE_SHIFT",
      payload: shift,
    });
    dispatch(updateMemberShiftCounts());
  };
};

export const deleteShift = (shiftId) => {
  return (dispatch) => {
    dispatch({
      type: "SHIFT_DELETE_SHIFT",
      payload: shiftId,
    });
    dispatch(updateMemberShiftCounts());
  };
};

export const clearAllShifts = () => {
  return (dispatch) => {
    dispatch({
      type: "SHIFT_CLEAR_ALL_SHIFTS",
    });
    dispatch(updateMemberShiftCounts());
  };
};

export const fetchShifts = (url) => {
  return (dispatch, getState) => {
    return getJSON(url)
      .then((shifts) => {
        // Add custom identifiers and convert dates to moment objects
        const state = getState();
        let currentIndex = state.member.current_index;
        
        const processedShifts = shifts.map((shift) => {
          if (!shift.identifier) {
            currentIndex += 1;
            shift.identifier = currentIndex;
          }
          
          // Convert to DateTime objects
          return {
            ...shift,
            start_time: DateTime.fromISO(shift.start_time),
            end_time: DateTime.fromISO(shift.end_time),
            lunch: shift.lunch ? DateTime.fromISO(shift.lunch) : null,
            dinner: shift.dinner ? DateTime.fromISO(shift.dinner) : null,
            members: shift.members || [],
            preferred_members: shift.preferred_members || [],
          };
        });
        
        // Create deep copy for original_shifts
        const originalShifts = processedShifts.map(shift => ({...shift}));
        
        dispatch(setShifts({ 
          shifts: processedShifts,
          original_shifts: originalShifts
        }));
        dispatch(updateMemberShiftCounts());
        
        return processedShifts;
      })
      .catch((error) => {
        console.error("Error fetching shifts:", error);
        dispatch(setLoading(false));
      });
  };
};

export const updateMemberShiftCounts = () => {
  return (dispatch, getState) => {
    const state = getState();
    const members = Object.values(state.member.members_by_id);
    const shifts = state.member.shifts;
    
    if (members.length === 0) {
      return;
    }
    
    // Reset current counts
    members.forEach(member => {
      dispatch(updateMember(member.id, {
        current_shift_count: 0,
        current_hour_count: 0,
        current_preferred_shift_count: 0,
        current_preferred_hour_count: 0,
      }));
    });

    let newShiftCount = {};
    let newHourCount = {};
    let newPreferredShiftCount = {};
    let newPreferredHourCount = {};
    
    // Calculate counts from current shifts
    shifts.forEach(shift => {
      const shiftMembersIds = (shift.members || []).map(m => m.id);
      const shiftPreferredMemberIds = (shift.preferred_members || []).map(m => m.id);
      
      // Calculate duration in hours
      const startTime = shift.start_time instanceof DateTime ? shift.start_time : DateTime.fromISO(shift.start_time);
      const endTime = shift.end_time instanceof DateTime ? shift.end_time : DateTime.fromISO(shift.end_time);
      const duration = endTime.diff(startTime, 'hours').hours;
      
      members.forEach(member => {
        const isAssigned = shiftMembersIds.includes(member.id);
        const isInPreferred = shiftPreferredMemberIds.includes(member.id);
        
        if (isAssigned) {
          newShiftCount[member.id] = (newShiftCount[member.id] || 0) + 1;
          newHourCount[member.id] = Math.round(((newHourCount[member.id] || 0) + duration) * 10) / 10;
        }
        
        if (isInPreferred && !isAssigned) {
          newPreferredShiftCount[member.id] = (newPreferredShiftCount[member.id] || 0) + 1;
          newPreferredHourCount[member.id] = Math.round(((newPreferredHourCount[member.id] || 0) + duration) * 10) / 10;

        }
      });
    });

    members.forEach(member => {
      dispatch(updateMember(member.id, {
        current_shift_count: newShiftCount[member.id] || 0,
        current_hour_count: newHourCount[member.id] || 0,
        current_preferred_shift_count: newPreferredShiftCount[member.id] || 0,
        current_preferred_hour_count: newPreferredHourCount[member.id] || 0,
        total_shift_count: (newShiftCount[member.id] || 0) + member.existing_shift_count,
        total_hour_count: (newHourCount[member.id] || 0) + member.existing_hour_count,
        total_preferred_shift_count: (newPreferredShiftCount[member.id] || 0) + member.existing_preferred_shift_count,
        total_preferred_hour_count: (newPreferredHourCount[member.id] || 0) + member.existing_preferred_hour_count,
      }));
    });
  };
};

export const setPeriods = (periods) => ({
  type: "MEMBER_SET_PERIODS",
  payload: periods,
});

export const setLoading = (loading) => ({
  type: "MEMBER_SET_LOADING",
  payload: loading,
});

export const setDisplayGlobal = (value) => ({
  type: "MEMBER_SET_DISPLAY_GLOBAL",
  payload: value,
});

export const setFilter = (key, value) => ({
  type: "MEMBER_SET_FILTER",
  payload: { key, value },
});

export const setFilters = (filters) => ({
  type: "MEMBER_SET_FILTERS",
  payload: filters,
});

export const resetFilters = () => ({
  type: "MEMBER_RESET_FILTERS",
});

export const setHiddenCount = (count) => ({
  type: "MEMBER_SET_HIDDEN_COUNT",
  payload: count,
});

export const addMemberToSelection = (memberId) => ({
  type: "MEMBER_ADD_TO_SELECTION",
  payload: memberId,
});

export const removeMemberFromSelection = (memberId) => ({
  type: "MEMBER_REMOVE_FROM_SELECTION",
  payload: memberId,
});

export const clearMemberSelection = () => ({
  type: "MEMBER_CLEAR_SELECTION",
});

export const setSelectedMembers = (members) => ({
  type: "MEMBER_SET_SELECTED_MEMBERS",
  payload: members,
});
